import { useEffect, useRef, useState } from "react";
import { NetworkGraphData, NetworkNode } from "../../types";

interface NetworkGraphProps {
  data: NetworkGraphData;
  height?: number;
}

interface NodePosition extends NetworkNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export default function NetworkGraph({
  data,
  height = 600,
}: NetworkGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [nodes, setNodes] = useState<NodePosition[]>([]);
  const [hoveredNode, setHoveredNode] = useState<NodePosition | null>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!data.nodes.length) return;

    const width = canvasRef.current?.offsetWidth || 800;
    const centerX = width / 2;
    const centerY = height / 2;

    // Initialize nodes with positions
    const initialNodes: NodePosition[] = data.nodes.map((node, i) => {
      const angle = (i / data.nodes.length) * Math.PI * 2;
      const radius = Math.min(width, height) * 0.35;
      return {
        ...node,
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        vx: 0,
        vy: 0,
      };
    });

    setNodes(initialNodes);
  }, [data, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !nodes.length) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.offsetWidth;
    canvas.width = width;
    canvas.height = height;

    let currentNodes = [...nodes];

    const simulate = () => {
      const nodeMap = new Map(currentNodes.map((n) => [n.id, n]));

      // Apply forces
      currentNodes.forEach((node, i) => {
        let fx = 0,
          fy = 0;

        // Repulsion between nodes
        currentNodes.forEach((other, j) => {
          if (i === j) return;
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const dist = Math.hypot(dx, dy) || 1;
          const force = 500 / (dist * dist);
          fx += (dx / dist) * force;
          fy += (dy / dist) * force;
        });

        // Attraction along edges
        data.edges.forEach((edge) => {
          let source, target;
          if (edge.source === node.id) {
            source = node;
            target = nodeMap.get(edge.target);
          } else if (edge.target === node.id) {
            source = node;
            target = nodeMap.get(edge.source);
          }

          if (source && target) {
            const dx = target.x - source.x;
            const dy = target.y - source.y;
            const dist = Math.hypot(dx, dy) || 1;
            const force = dist * 0.001;
            fx += (dx / dist) * force;
            fy += (dy / dist) * force;
          }
        });

        // Center attraction
        const dx = width / 2 - node.x;
        const dy = height / 2 - node.y;
        fx += dx * 0.0001;
        fy += dy * 0.0001;

        node.vx = (node.vx + fx) * 0.85;
        node.vy = (node.vy + fy) * 0.85;

        node.x += node.vx;
        node.y += node.vy;

        // Keep nodes in bounds
        node.x = Math.max(30, Math.min(width - 30, node.x));
        node.y = Math.max(30, Math.min(height - 30, node.y));
      });

      // Render
      ctx.clearRect(0, 0, width, height);

      // Draw edges
      data.edges.forEach((edge) => {
        const source = nodeMap.get(edge.source);
        const target = nodeMap.get(edge.target);
        if (!source || !target) return;

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);

        if (edge.active) {
          // Gradiente para conexões ativas
          const gradient = ctx.createLinearGradient(
            source.x,
            source.y,
            target.x,
            target.y,
          );
          gradient.addColorStop(0, "rgba(168, 85, 247, 0.6)");
          gradient.addColorStop(0.5, "rgba(139, 92, 246, 0.5)");
          gradient.addColorStop(1, "rgba(168, 85, 247, 0.6)");
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2.5;
        } else {
          ctx.strokeStyle = "rgba(148, 163, 184, 0.2)";
          ctx.lineWidth = 1;
        }

        ctx.stroke();
      });

      // Draw nodes with glow effect
      currentNodes.forEach((node) => {
        const radius = 10;
        const isHovered = hoveredNode?.id === node.id;

        // Glow effect
        if (isHovered) {
          ctx.shadowBlur = 20;
          ctx.shadowColor =
            node.type === "professional"
              ? "rgba(168, 85, 247, 0.8)"
              : "rgba(56, 189, 248, 0.8)";
        }

        // Gradiente radial para os nós
        const gradient = ctx.createRadialGradient(
          node.x - 3,
          node.y - 3,
          0,
          node.x,
          node.y,
          radius,
        );

        if (node.type === "professional") {
          if (isHovered) {
            gradient.addColorStop(0, "#c084fc");
            gradient.addColorStop(0.5, "#a855f7");
            gradient.addColorStop(1, "#7c3aed");
          } else {
            gradient.addColorStop(0, "#a855f7");
            gradient.addColorStop(0.5, "#9333ea");
            gradient.addColorStop(1, "#7c3aed");
          }
        } else {
          if (isHovered) {
            gradient.addColorStop(0, "#7dd3fc");
            gradient.addColorStop(0.5, "#38bdf8");
            gradient.addColorStop(1, "#0284c7");
          } else {
            gradient.addColorStop(0, "#38bdf8");
            gradient.addColorStop(0.5, "#0ea5e9");
            gradient.addColorStop(1, "#0284c7");
          }
        }

        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Borda com cor mais vibrante
        ctx.strokeStyle = isHovered
          ? node.type === "professional"
            ? "#e9d5ff"
            : "#dbeafe"
          : "#ffffff";
        ctx.lineWidth = isHovered ? 3 : 2;
        ctx.stroke();

        // Reset shadow
        ctx.shadowBlur = 0;

        // Draw label com background
        if (isHovered) {
          const labelPadding = 8;
          const labelHeight = 18;
          const textMetrics = ctx.measureText(node.label);
          const labelWidth = textMetrics.width + labelPadding * 2;

          // Background do label
          ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
          ctx.fillRect(
            node.x - labelWidth / 2,
            node.y - radius - labelHeight - 8,
            labelWidth,
            labelHeight,
          );

          // Texto do label
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 12px Inter, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(
            node.label,
            node.x,
            node.y - radius - labelHeight / 2 - 8,
          );
        }
      });

      animationRef.current = requestAnimationFrame(simulate);
    };

    simulate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [nodes, data.edges, hoveredNode, height]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const found = nodes.find((node) => {
      const dx = node.x - x;
      const dy = node.y - y;
      return Math.hypot(dx, dy) <= 10;
    });

    setHoveredNode(found || null);
  };

  if (!data.nodes.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Conexões
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nenhuma conexão entre profissionais e alunos encontrada para o período
          selecionado.
        </p>
      </div>
    );
  }

  const professionalsCount = data.nodes.filter(
    (n) => n.type === "professional",
  ).length;
  const studentsCount = data.nodes.filter((n) => n.type === "student").length;
  const activeConnections = data.edges.filter((e) => e.active).length;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-lg font-bold text-transparent dark:from-purple-400 dark:to-blue-400">
            Conexões Profissionais/Alunos
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Visualização interativa das conexões entre profissionais e alunos
          </p>
        </div>
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-1.5 dark:bg-purple-950/30">
            <div className="h-3 w-3 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg shadow-purple-500/50"></div>
            <span className="font-medium text-purple-700 dark:text-purple-300">
              Profissionais ({professionalsCount})
            </span>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-1.5 dark:bg-sky-950/30">
            <div className="h-3 w-3 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 shadow-lg shadow-sky-500/50"></div>
            <span className="font-medium text-sky-700 dark:text-sky-300">
              Alunos ({studentsCount})
            </span>
          </div>
        </div>
      </div>

      <div className="mb-3 flex justify-between rounded-lg bg-gradient-to-r from-purple-50 to-sky-50 px-4 py-2 text-xs dark:from-purple-950/20 dark:to-sky-950/20">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          Total de conexões:{" "}
          <span className="font-bold text-purple-600 dark:text-purple-400">
            {data.edges.length}
          </span>
        </span>
        <span className="font-medium text-gray-700 dark:text-gray-300">
          Conexões ativas:{" "}
          <span className="font-bold text-green-600 dark:text-green-400">
            {activeConnections}
          </span>
        </span>
      </div>

      <div className="relative">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredNode(null)}
          style={{ width: "100%", height: `${height}px`, cursor: "pointer" }}
          className="rounded-lg bg-gradient-to-br from-slate-50 via-purple-50/30 to-sky-50/30 dark:from-slate-900 dark:via-purple-950/20 dark:to-sky-950/20"
        />
        {hoveredNode && (
          <div
            className="absolute right-4 top-4 rounded-xl border-2 bg-white/95 p-4 shadow-2xl backdrop-blur-sm dark:bg-gray-900/95"
            style={{
              borderColor:
                hoveredNode.type === "professional"
                  ? "rgb(168, 85, 247)"
                  : "rgb(56, 189, 248)",
            }}
          >
            <p
              className="text-sm font-bold"
              style={{
                color:
                  hoveredNode.type === "professional"
                    ? "rgb(168, 85, 247)"
                    : "rgb(56, 189, 248)",
              }}
            >
              {hoveredNode.label}
            </p>
            <p className="mt-2 text-xs font-medium text-gray-600 dark:text-gray-300">
              {hoveredNode.type === "professional" ? "Profissional" : "Aluno"}
            </p>
            <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
              ID: {hoveredNode.user_id}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
